import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import { PageLoader } from './ui/PageLoader';
import { AdminQueueState } from './ui/AdminQueueState';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';

interface ProductUpdate {
  id: number;
  title: string;
  body?: string | null;
  href?: string | null;
  published_at?: string | null;
}

const AdminProductUpdates: React.FC = () => {
  const { showToast } = useToast();
  const [items, setItems] = useState<ProductUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [href, setHref] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductUpdate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminListProductUpdates();
      setItems(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load product updates');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiService.adminCreateProductUpdate({
        title: title.trim(),
        body: body.trim() || undefined,
        href: href.trim() || undefined,
        published_at: new Date().toISOString(),
      });
      setTitle('');
      setBody('');
      setHref('');
      showToast('Product update published.', 'success');
      await load();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create';
      setError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.adminDeleteProductUpdate(deleteTarget.id);
      showToast('Update deleted.', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete';
      setError(message);
      showToast(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <PageHeader
        eyebrow="Admin"
        title="Product updates"
        description="Short announcements shown on the authenticated homepage."
        backLink={{ to: '/admin', label: 'Overview' }}
      />

      {loading ? <PageLoader message="Loading updates" /> : null}

      {!loading && error ? (
        <AdminQueueState
          variant="error"
          title="Could not load product updates"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      <form onSubmit={create} className="form-section space-y-3">
        <label className="block">
          <span className="sr-only">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="input"
            required
          />
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body"
          rows={3}
          className="input"
          aria-label="Body"
        />
        <input
          type="text"
          value={href}
          onChange={(e) => setHref(e.target.value)}
          placeholder="Optional link path (e.g. /events)"
          className="input"
          aria-label="Link"
        />
        <Button type="submit" variant="primary" loading={saving}>
          Publish
        </Button>
      </form>

      {!loading && !error && items.length === 0 ? (
        <AdminQueueState
          variant="empty"
          title="No product updates yet"
          description="Publish a short homepage announcement to get started."
        />
      ) : null}

      {!loading && items.length > 0 ? (
        <ul className="divide-y divide-white/[0.06] border border-white/[0.08] rounded-xl overflow-hidden">
          {items.map((u) => (
            <li key={u.id} className="px-4 py-3 flex items-start justify-between gap-3 bg-[#0c0c0c]/50">
              <div className="min-w-0">
                <p className="text-white font-medium text-sm">{u.title}</p>
                {u.body ? <p className="text-gray-400 text-xs mt-1">{u.body}</p> : null}
              </div>
              <Button type="button" variant="danger" size="sm" onClick={() => setDeleteTarget(u)}>
                Delete
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete product update?"
        description={
          deleteTarget ? (
            <>
              Remove <strong className="text-white">{deleteTarget.title}</strong> from the homepage
              feed. This cannot be undone.
            </>
          ) : undefined
        }
        confirmLabel="Delete update"
        cancelLabel="Cancel"
        destructive
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
};

export default AdminProductUpdates;
